import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { settingsApi } from '@/services/api';

const DEFAULT_BEFORE = 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=1200';
const DEFAULT_AFTER = 'https://images.pexels.com/photos/29925933/pexels-photo-29925933.jpeg?w=1200';

const BeforeAfterSection = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [sectionSettings, setSectionSettings] = useState({
    before: DEFAULT_BEFORE,
    after: DEFAULT_AFTER,
    titleEn: '',
    titleAr: '',
  });
  const [position, setPosition] = useState(50);
  const [aspectRatio, setAspectRatio] = useState(3 / 2);
  const [dragging, setDragging] = useState(false);
  const containerRef = useRef(null);
  const draggingRef = useRef(false);

  useEffect(() => {
    settingsApi.get().then((res) => {
      const s = res.data;
      setSectionSettings({
        before: s.transformation_before_image || DEFAULT_BEFORE,
        after: s.transformation_after_image || DEFAULT_AFTER,
        titleEn: s.transformation_title_en || '',
        titleAr: s.transformation_title_ar || '',
      });
    }).catch(() => {});
  }, []);

  const updateFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width === 0) return;
    let pct = ((clientX - rect.left) / rect.width) * 100;
    pct = Math.max(0, Math.min(100, pct));
    setPosition(pct);
  }, []);

  useEffect(() => {
    const clientXFrom = (e) => (e.touches && e.touches[0] ? e.touches[0].clientX : e.clientX);
    const onMove = (e) => {
      if (!draggingRef.current) return;
      if (e.cancelable) e.preventDefault();
      updateFromClientX(clientXFrom(e));
    };
    const onUp = () => {
      draggingRef.current = false;
      setDragging(false);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchend', onUp);
    };
  }, [updateFromClientX]);

  const startDrag = (e) => {
    draggingRef.current = true;
    setDragging(true);
    const clientX = e.touches && e.touches[0] ? e.touches[0].clientX : e.clientX;
    updateFromClientX(clientX);
  };

  const handleBaseLoad = (e) => {
    const { naturalWidth, naturalHeight } = e.target;
    if (naturalWidth > 0 && naturalHeight > 0) {
      setAspectRatio(naturalWidth / naturalHeight);
    }
  };

  const displayTitle = isRTL
    ? (sectionSettings.titleAr || t('beforeAfter.title'))
    : (sectionSettings.titleEn || t('beforeAfter.title'));

  return (
    <section data-testid="before-after-section" className="section-padding bg-secondary">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="label-text mb-4 block">{displayTitle}</span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight">
            {displayTitle}
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="max-w-5xl mx-auto"
        >
          <div
            ref={containerRef}
            data-testid="before-after-slider"
            onMouseDown={startDrag}
            onTouchStart={startDrag}
            className="relative w-full overflow-hidden select-none"
            style={{ aspectRatio: String(aspectRatio), touchAction: 'none', cursor: 'ew-resize' }}
          >
            {/* After image (base layer) */}
            <img
              src={sectionSettings.after}
              alt={t('beforeAfter.after')}
              onLoad={handleBaseLoad}
              draggable={false}
              className="absolute inset-0 w-full h-full object-cover pointer-events-none"
            />
            {/* Before image, revealed up to the slider position */}
            <img
              src={sectionSettings.before}
              alt={t('beforeAfter.before')}
              draggable={false}
              className="absolute inset-0 w-full h-full object-cover pointer-events-none"
              style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}
            />

            {/* Labels */}
            <div
              className="absolute top-1/2 -translate-y-1/2 px-4 py-2 text-white text-sm pointer-events-none transition-opacity"
              style={{ left: '5%', background: 'rgba(0,0,0,0.5)', opacity: dragging ? 0 : 1 }}
            >
              {t('beforeAfter.before')}
            </div>
            <div
              className="absolute top-1/2 -translate-y-1/2 px-4 py-2 text-white text-sm pointer-events-none transition-opacity"
              style={{ right: '5%', background: 'rgba(0,0,0,0.5)', opacity: dragging ? 0 : 1 }}
            >
              {t('beforeAfter.after')}
            </div>

            {/* Slider line + handle */}
            <div
              className="absolute top-0 bottom-0 flex items-center justify-center pointer-events-none"
              style={{ left: `${position}%`, transform: 'translateX(-50%)', width: '48px' }}
            >
              <div style={{ width: '3px', height: '100%', background: '#ef7f1a' }} />
              <div className="absolute w-12 h-12 bg-gold rounded-full flex items-center justify-center shadow-lg">
                <div className="flex gap-1">
                  <div className="w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-r-[8px] border-r-charcoal" />
                  <div className="w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-l-[8px] border-l-charcoal" />
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default BeforeAfterSection;
